/**
 * Story 057 follow-up (2026-05-14) — Type-cell drill-down affordance.
 *
 * Renders the turbine-model name as an underlined `<button>` so Tab
 * reaches it and Enter/Space activate the drill-down. Click opens the
 * Story 059 Edit/View Turbine Model modal in-place over the Turbines
 * list — the page mounts `<EditTurbineModelDialog>` at its root and
 * keys it on the `?model=<uuid>` URL param. Mirrors `<ParkCell>`.
 */
import { Box } from "@mui/material";
import type { MouseEvent, ReactElement } from "react";

export interface TypeCellProps {
  modelName: string;
  onOpen: () => void;
}

export function TypeCell({ modelName, onOpen }: TypeCellProps): ReactElement {
  function handleClick(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    onOpen();
  }
  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      // Underline colours from the JS palette. `ParkCell`'s header explains
      // the defect this fixes: `var(--mui-palette-*)` is never emitted by a
      // plain `createTheme()`, so the underline fell back to `currentColor`
      // and disappeared into the text (SB6-G2 Bug-7). ParkCell was fixed at
      // the time; this file was the one its comment pointed AT as still broken.
      sx={(theme) => ({
        background: "none",
        border: "none",
        padding: 0,
        color: "text.primary",
        cursor: "pointer",
        textAlign: "inherit",
        fontFamily: "inherit",
        fontSize: "inherit",
        textDecoration: "underline",
        textUnderlineOffset: "3px",
        textDecorationColor: theme.palette.text.secondary,
        "&:hover, &:focus-visible": {
          textDecorationColor: theme.palette.primary.main,
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "2px",
          borderRadius: "2px",
        },
      })}
    >
      {modelName}
    </Box>
  );
}
